// ==================================================================================
// Digital Approval System (DAS) - ReportPageLayout
// ==================================================================================
// Standard layout for a single report page: ModuleHeader on top (with refresh
// and export-to-excel actions), content area below.
// ==================================================================================

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';

export interface ReportPageLayoutProps {
  title: string;
  description?: string;

  onRefresh: () => void;
  onExport?: () => void;
  exportDisabled?: boolean;

  loading?: boolean;
  error?: string | null;

  children: React.ReactNode;
}

const errorBannerStyle: React.CSSProperties = {
  background: '#fee2e2',
  color: '#991b1b',
  border: '1px solid #fecaca',
  borderRadius: '0.375rem',
  padding: '0.625rem 0.875rem',
  margin: '0.5rem 0',
  fontSize: '0.85rem',
};

const loadingBannerStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: '#6b7280',
  padding: '0.25rem 0',
};

const contentWrapperStyle: React.CSSProperties = {
  padding: '0.75rem 1rem 1.5rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

const ReportPageLayout: React.FC<ReportPageLayoutProps> = ({
  title,
  description,
  onRefresh,
  onExport,
  exportDisabled,
  loading,
  error,
  children,
}) => {
  const actions: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={16} className={loading ? 'spinner' : ''} />,
      label: 'Refresh',
      onClick: onRefresh,
      tooltip: 'Reload report data',
      variant: 'icon',
      disabled: loading,
      visible: true,
    },
    ...(onExport
      ? [
          {
            id: 'export',
            icon: <IconSystem.download size={16} />,
            label: 'Export',
            onClick: onExport,
            tooltip: 'Export current view to Excel',
            variant: 'secondary' as const,
            disabled: !!exportDisabled || !!loading,
            visible: true,
          },
        ]
      : []),
  ];

  return (
    // height:100% so we fill `.enterprise-module` (display:block, height:100%);
    // the inner content div is the flex column that gets the scroll behavior.
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <ModuleHeader
        title={title}
        description={description}
        actions={actions}
        visible={true}
      />
      <div style={contentWrapperStyle}>
        {error && <div style={errorBannerStyle}>{error}</div>}
        {loading && !error && <div style={loadingBannerStyle}>Loading report…</div>}
        {children}
      </div>
    </div>
  );
};

export default ReportPageLayout;
